import { ChangeDetectionStrategy, Component, inject, input, OnInit, output } from '@angular/core';
import { AbstractControl, FormGroup, FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ButtonLoadingDirective } from '@app/shared/directives/button-loading/button-loading.directive';
import { kendoIcons } from '@app/shared/library/kendo-icons';
import { ProjectWellPadParameter } from '@app/shared/models/project-well-pad-parameter';
import { ButtonDirective } from '@progress/kendo-angular-buttons';
import { DropDownListComponent } from '@progress/kendo-angular-dropdowns';
import { zip as observableZip } from 'rxjs';
import { LookupPlusService } from '../../services/lookups/lookup-plus.service';
import { ProjectCustomEndpointService } from '../../services/project-custom-endpoint.service';
import { ProjectEstimateService } from './../../services/project-estimate.service';

@Component({
  selector: 'app-project-well-pad-details',
  templateUrl: './project-well-pad-details.component.html',
  styleUrls: ['./project-well-pad-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonDirective,
    ButtonLoadingDirective,
    FormsModule,
    DropDownListComponent,
    ReactiveFormsModule,
  ],
})
export class ProjectWellPadDetailsComponent implements OnInit {
  private fb = inject(UntypedFormBuilder);
  private lookupPlusService = inject(LookupPlusService);
  private projectCustomEndpointService = inject(ProjectCustomEndpointService);
  private estimationService = inject(ProjectEstimateService);

  public wellPadForm: UntypedFormGroup;
  public dCellarPadVisitTypes: any[] = [];
  public dTankBatteryDesigns: any[] = [];
  public wellPadParameters: ProjectWellPadParameter;
  public yesNoResponse: Array<any> = [{ text: 'Yes', value: true }, { text: 'No', value: false }];
  public wellTesterError: string = "";
  public loading: boolean = false;
  public icons = kendoIcons;

  readonly tabTdv = output<boolean>();
  readonly iProjectId = input<number>(undefined);
  readonly iProjectTypeId = input<number>(undefined);
  readonly iProjectStatusId = input<number>(undefined);

  ngOnInit() {
    this.generateForm();
    this.getCellarPadVisitTypes();
    this.getTankBatteryDesigns();
    this.getWellPadParameters(false);
  }

  public isTesterWellCountMatched(control: FormGroup): { [key: string]: any; } {
    if ((typeof control.parent) === "object") {
      var ProjectWellCountControl: AbstractControl = control.controls["ProjectWellCount"];
      var Tester_PermanentControl: AbstractControl = control.controls["Tester_Permanent"];
      var Tester_MPFMeterControl: AbstractControl = control.controls["Tester_MPFMeter"];

      var ProjectWellCount: number = ProjectWellCountControl.value;
      var Tester_Permanent: number = Tester_PermanentControl.value;
      var Tester_MPFMeter: number = Tester_MPFMeterControl.value;

      var wellTesterCount = Tester_Permanent + Tester_MPFMeter;
      return ((ProjectWellCount > 0) && (ProjectWellCount > wellTesterCount)) ?
        { 'testerWellCountMismatch': ProjectWellCount + ' wells have been allocated to the project but are only ' + wellTesterCount + ' wells are assigned testers' } :
        null;
    }
    return null;
  };

  public updateWellPadParameters(e): void {
    const requests = [];
    this.wellPadParameters.CellarPadVisitTypeId = this.wellPadForm.controls.CellarPadVisitTypeId.value;
    this.wellPadParameters.AssociatedTankBatteryDesignId = this.wellPadForm.controls.AssociatedTankBatteryDesignId.value;
    this.wellPadParameters.IsLocatedOnTankBatteryPad = this.wellPadForm.controls.IsLocatedOnTankBatteryPad.value;

    this.wellPadParameters.Tester_Permanent = this.wellPadForm.controls.Tester_Permanent.value;
    this.wellPadParameters.Tester_MPFMeter = this.wellPadForm.controls.Tester_MPFMeter.value;

    if (this.wellPadForm.dirty && this.wellPadForm.controls.WellToEdgeOfPadDistance.value == null) {
      this.wellPadForm.controls.WellToEdgeOfPadDistance.setValue(0)
    }

    requests.push(this.projectCustomEndpointService.patch(`ProjectWellPadParameters(${this.wellPadParameters.ProjectWellPadParameterId})`, this.wellPadForm.value));
    observableZip(...requests).subscribe(response => {
      this.wellPadForm.reset();
      this.getWellPadParameters(false);
    });
  }

  public cancelChanges(e): void {
    this.wellPadForm.reset();
    this.getWellPadParameters(false);
  }

  public setTDV(): void {
    let tdvState: boolean = true;
    this.tabTdv.emit(tdvState);
  }

  public clearTDV(): void {
    let tdvState: boolean = false;
    this.tabTdv.emit(tdvState);
  }

  public getWellPadParameters(refreshParam: boolean) {
    this.loading = true;
    this.estimationService.calculateWellPadParameters(this.iProjectId(), refreshParam)
      .then(() => {
        this.lookupPlusService.getLookup('ProjectWellPadParameters', `&$filter=ProjectId eq ${this.iProjectId()}`)
          .then(payload => {
            this.wellPadParameters = payload.value[0];
            this.loading = false;
            if (this.wellPadParameters == null)
              return;
            this.wellPadForm.patchValue(this.wellPadParameters);
          });
      });
  }

  private generateForm(): void {
    this.wellPadForm = this.fb.group({
      Tester_Permanent: [null],
      Tester_MPFMeter: [null],

      CellarPadVisitTypeId: 4,
      AssociatedTankBatteryDesignId: null,
      IsLocatedOnTankBatteryPad: false,
      ProjectWellCount: null,
      ESPWellCount: null,
      ESP7WellCount: null,
      HPGLWellCount: null,
      ESPHRWellCount: null,
      GasLiftWellCount: null,
      BelowGradeWellCount: null,
      AboveGradeWellCount: null,
      DualFlowWellCount: null,
      WellToEdgeOfPadDistance: [0]
    });
    this.wellPadForm.valueChanges
      .subscribe(data => this.onValueChanged(data));
  }

  private onValueChanged(data?: any) {
    let isValid: boolean = false;

    if (!this.wellPadForm) {
      return;
    }
    let Tester_MPFMeter: number = this.wellPadForm.controls.Tester_MPFMeter.value || 0;
    let Tester_Permanent: number = this.wellPadForm.controls.Tester_Permanent.value || 0;
    let wellTesterCount = Tester_Permanent + Tester_MPFMeter;
    let ProjectWellCount: number = this.wellPadForm.controls.ProjectWellCount.value || 0;

    if ((ProjectWellCount > 0) && (ProjectWellCount > wellTesterCount)) {
      this.wellTesterError = `Warning: ${ProjectWellCount} wells have been allocated to the project but only ${wellTesterCount} wells are assigned testers`;
      this.wellPadForm.controls.ProjectWellCount.setErrors(null);
    } else {
      this.wellTesterError = "";
      this.wellPadForm.controls.ProjectWellCount.setErrors(null);
    }

    if (this.wellPadForm.valid && this.wellPadForm.dirty) {
      isValid = true;
      this.setTDV();
    } else {
      isValid = false;
      this.clearTDV();
    }
  }

  private getCellarPadVisitTypes() {
    this.lookupPlusService.getLookupNoCount('CellarPadVisitTypes').then(payload => {
      this.dCellarPadVisitTypes = payload.value;
    });
  }

  private getTankBatteryDesigns() {
    this.lookupPlusService.getLookupNoCount('TankBatteryDesigns').then(payload => {
      this.dTankBatteryDesigns = payload.value;
    });
  }
}